import {css} from '@mui/material';
import {DlMuiDynamicIcon, TooltipWrapperCore} from '@wallaby-ui/core';
import React, {useLayoutEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {AlertBoardKpiDto} from './alert_board_types';
import {ALERT_EMPTY_VALUE, formatAlertAvgDays, formatAlertSolvedRate} from './alert_format';
import {ALERT_DISPLAYED_STATE_ENUM} from './alert_types';
import {useFormatters} from '../LightFormatter';

type Props = {
  /** Undefined while the board loads: every tile shows the placeholder. */
  kpis: AlertBoardKpiDto | undefined;
  /** Include deleted state: a deletion counts as a resolution, which changes the solved-rate formula in its tooltip. */
  includeDeleted: boolean;
};

type Tile = {
  id: string;
  label: string;
  value: string;
  iconName: string;
  viewBox: string;
  /** The icon square's background and glyph colour (Alert Board design). */
  background: string;
  color: string;
  tip?: string;
};

/* the icon square (34px) and its gap to the value (10px): the room a tile gives up when the icons are hidden */
const ICON_ROOM = 34 + 10;

const stripCss = css`
  /* a strip too narrow for icon and value side by side drops the icons, so the value keeps the room */
  &[data-without-icons='true'] [data-name='dl-alert-board-kpi--icon'] {
    display: none;
  }
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
`;
const tileCss = css`
  min-width: 0;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 22px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;
const valueRowCss = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const iconWrapCss = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 9px;
`;
const iconCss = css`
  font-size: 14px;
`;
const valueCss = css`
  font-size: 34px;
  line-height: 1;
  font-family: var(--dlFontFamilySemiBold);
  color: var(--dlThemeGray-900);
  white-space: nowrap;
`;
const labelRowCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
`;
const labelCss = css`
  font-size: 11.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dlThemeGray-500);
  font-family: var(--dlFontFamilySemiBold);
`;
const infoCss = css`
  font-size: 11px;
`;
const tipWrapperCss = css`
  display: inline-flex;
  color: var(--dlThemeGray-300);
  cursor: help;
`;

/**
 * The alert board's KPI strip (PDW-184): Defined alerts, Active episodes, Snoozed, Solved rate and Avg time to solve,
 * over the whole board's active alerts as core counts them. Solved rate and Avg time to solve carry an info tooltip
 * with the formula in force, which Include deleted state switches.
 */
export const AlertBoardKpiStrip: React.FC<Props> = (props) => {
  const {kpis, includeDeleted} = props;
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({id}, values);
  const {tEnum} = useFormatters();
  const count = (value: number | undefined) => (value == null ? ALERT_EMPTY_VALUE : intl.formatNumber(value));
  const avgDays = formatAlertAvgDays(kpis?.avgDaysToSolve, intl);
  const tiles: Tile[] = [
    {
      id: 'definedAlerts',
      label: t('alert.board.kpi.definedAlerts'),
      value: count(kpis?.definedAlerts),
      iconName: 'bellIcon',
      viewBox: '0 0 448 512',
      background: 'var(--dlTheme-50)',
      color: 'var(--dlTheme-600)',
    },
    {
      id: 'activeEpisodes',
      label: t('alert.board.kpi.activeEpisodes'),
      value: count(kpis?.activeEpisodes),
      iconName: 'boltIcon',
      viewBox: '0 0 448 512',
      background: 'var(--dlAlertActiveBackground)',
      color: 'var(--dlAlertActiveColor)',
    },
    {
      id: 'snoozedEpisodes',
      label: tEnum(ALERT_DISPLAYED_STATE_ENUM, 'SNOOZED'),
      value: count(kpis?.snoozedEpisodes),
      iconName: 'clockIcon',
      viewBox: '0 0 512 512',
      background: 'var(--dlAlertSnoozedBackground)',
      color: 'var(--dlAlertSnoozedColor)',
    },
    {
      id: 'solvedRate',
      label: t('alert.board.kpi.solvedRate'),
      value: formatAlertSolvedRate(kpis?.solvedRate, intl),
      iconName: 'checkSolidIcon',
      viewBox: '0 0 448 512',
      background: 'var(--dlAlertSolvedBackground)',
      color: 'var(--dlAlertSolvedColor)',
      tip: t(includeDeleted ? 'alert.board.kpi.solvedRate.tip.withDeleted' : 'alert.board.kpi.solvedRate.tip'),
    },
    {
      id: 'avgDaysToSolve',
      label: t('alert.board.kpi.avgTimeToSolve'),
      value: avgDays == null ? ALERT_EMPTY_VALUE : t('alert.board.days', {days: avgDays}),
      iconName: 'hourglassHalfIcon',
      viewBox: '0 0 384 512',
      background: 'var(--dlThemeGray-100)',
      color: 'var(--dlThemeGray-600)',
      tip: t(includeDeleted ? 'alert.board.kpi.avgTimeToSolve.tip.withDeleted' : 'alert.board.kpi.avgTimeToSolve.tip'),
    },
  ];

  // The icons give way only when some tile cannot hold icon and value side by side. The value's own width does not
  // depend on the icon, so the check reads the same with the icons shown or hidden and never flips back and forth.
  const stripRef = useRef<HTMLDivElement>(null);
  const [withoutIcons, setWithoutIcons] = useState(false);
  const values = tiles.map((tile) => tile.value).join('|');
  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip) return undefined;
    const measure = () => {
      const tooNarrow = Array.from(strip.querySelectorAll<HTMLElement>("[data-name='dl-alert-board-kpi']")).some((tile) => {
        const value = tile.querySelector<HTMLElement>("[data-name='dl-alert-board-kpi--value']");
        if (!value) return false;
        const style = getComputedStyle(tile);
        const contentWidth = tile.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        return value.scrollWidth + ICON_ROOM > contentWidth;
      });
      setWithoutIcons(tooNarrow);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [values]);

  return (
    <div css={stripCss} ref={stripRef} data-name={'dl-alert-board-kpis'} data-without-icons={withoutIcons}>
      {tiles.map((tile) => (
        <div css={tileCss} key={tile.id} data-name={'dl-alert-board-kpi'} data-kpi={tile.id}>
          <div css={valueRowCss}>
            <span css={iconWrapCss} style={{background: tile.background, color: tile.color}} data-name={'dl-alert-board-kpi--icon'}>
              <DlMuiDynamicIcon
                iconName={tile.iconName}
                useFixedViewBox={tile.viewBox}
                noButtonStyles
                style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
                svgIconInputCss={iconCss}
              />
            </span>
            <span css={valueCss} data-name={'dl-alert-board-kpi--value'}>
              {tile.value}
            </span>
          </div>
          <div css={labelRowCss}>
            <span css={labelCss}>{tile.label}</span>
            {tile.tip && (
              <TooltipWrapperCore tooltip={tile.tip} wrapperCss={tipWrapperCss} placement='top'>
                <DlMuiDynamicIcon
                  iconName='circleInfoIcon'
                  useFixedViewBox='0 0 512 512'
                  noButtonStyles
                  style={{color: 'inherit', padding: 0, margin: 0}}
                  svgIconInputCss={infoCss}
                  data-name={'dl-alert-board-kpi--tip'}
                />
              </TooltipWrapperCore>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

AlertBoardKpiStrip.displayName = 'AlertBoardKpiStrip';
